import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const inputVariants = cva(
  'flex w-full text-foreground rounded-lg border border-border px-3 py-1 text-sm shadow-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-foreground-muted disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none',
  {
    variants: {
      variant: {
        default: 'border-border-strong bg-surface focus-visible:border-accent',
        ghost: 'border-transparent bg-transparent hover:bg-surface-hover',
        plain: 'border-transparent bg-transparent',
        onSurface:
          'border-border-strong bg-surface dark:bg-surface-active focus-visible:border-accent',
      },
      inputSize: {
        default: 'h-10',
        sm: 'h-8 text-xs',
        lg: 'h-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  }
);

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string;
  info?: string;
  error?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      inputSize,

      label,
      info,
      error,
      type,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    return (
      <div className="flex flex-col gap-2 w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="text-foreground text-sm font-sans"
          >
            {label}
          </label>
        )}
        <input
          type={type}
          id={inputId}
          className={cn(
            inputVariants({ variant, inputSize, className }),
            error && 'border-danger-8 focus-visible:border-danger-8'
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          ref={ref}
          {...props}
        />
        {info && (
          <p className="text-xs text-foreground-muted" role="note">
            {info}
          </p>
        )}
        {error && (
          <p id={errorId} aria-live="polite" className="text-danger-11 text-xs">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
